import { zodResolver } from "@hookform/resolvers/zod";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import SaveIcon from '@mui/icons-material/Save';
import { Box, Checkbox, CircularProgress, FormControlLabel, Grid2, IconButton, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useErrorHandler } from "../../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { getProtectionZoneFormSchema, ProtectionZoneForm as ProtectionZoneFormType } from "../../../types/forms/capture";
import { ProtectionZoneResponse } from "../../../types/model/protectionZone";

interface ProtectionZoneFormProps {
  protectionZoneData?: ProtectionZoneResponse | null;
  canEdit: boolean;
  onSubmit: (values: ProtectionZoneFormType) => Promise<void>;
}

export default function ProtectionZoneForm({
  protectionZoneData,
  canEdit,
  onSubmit,
}: Readonly<ProtectionZoneFormProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const [isUpdateForm, setIsUpdateForm] = useState<boolean>(!!protectionZoneData);
  const [isEditing, setIsEditing] = useState<boolean>(!isUpdateForm);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getProtectionZoneFormSchema(t);

  const methods = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      aImediata: protectionZoneData?.aImediata ?? null,
      pImediata: protectionZoneData?.pImediata ?? null,
      outrasCaptImediata: protectionZoneData?.outrasCaptImediata || "",
      snAimediata: protectionZoneData?.snAimediata ?? false,
      estado: protectionZoneData?.estado || "",

      rede: protectionZoneData?.rede ?? false,
      placa: protectionZoneData?.placa ?? false,
      nPlaca: protectionZoneData?.nPlaca ?? null,

      aIntermedia: protectionZoneData?.aIntermedia ?? null,
      outrasCaptIntermedia: protectionZoneData?.outrasCaptIntermedia || "",

      aAlargada: protectionZoneData?.aAlargada ?? null,
      outrasCaptAlargada: protectionZoneData?.outrasCaptAlargada || "",

      precoRede: protectionZoneData?.precoRede ?? null,
      precoPilaretes: protectionZoneData?.precoPilaretes ?? null,
      precoPlaca: protectionZoneData?.precoPlaca ?? null
    },
  });

  const handleFormSubmit = async (values: ProtectionZoneFormType) => {
    try {
      setIsSaving(true);

      await onSubmit(values);
      setIsEditing(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  // Sync form state when protectionZoneData changes
  useEffect(() => {
    setIsUpdateForm(!!protectionZoneData);
    if (protectionZoneData) {
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  }, [protectionZoneData]);

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Typography variant="h5">
                {t("captures.capture.protection-zone.title")}
              </Typography>

              {/* Action Icons */}
              {isEditing &&  canEdit ? ( 
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title={t("generic.save")}>
                    <IconButton
                      color="success"
                      onClick={methods.handleSubmit(handleFormSubmit)}
                      disabled={isSaving}
                    >
                      {isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={t("generic.cancel")}>
                    <IconButton
                      color="error"
                      onClick={() => {
                        setIsEditing(false);
                        methods.reset();
                      }}
                    >
                      <EditOffIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                canEdit && (
                  <Tooltip title={t("generic.edit")}>
                    <IconButton
                      color="primary"
                      onClick={() => setIsEditing(true)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                )
              )}
            </Box>
          </Grid2>

          <Grid2 size={12}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600, mb: 2 }}>
                {t("captures.capture.protection-zone.immediate-zone")}
              </Typography>

              <Grid2 container spacing={2}>
                <Grid2 size={3}>
                  <TextField
                    fullWidth
                    label={t("captures.capture.protection-zone.area")}                         
                    variant={(!isEditing || !canEdit) ? "standard" : "outlined"}
                    disabled={!isEditing || isSaving || !canEdit}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("aImediata")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.m2")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.aImediata}
                    helperText={methods.formState.errors.aImediata?.message}
                  />
                </Grid2>

                <Grid2 size={3}>
                  <TextField
                    fullWidth
                    label={t("captures.capture.protection-zone.p-imediata")}                         
                    variant={(!isEditing || !canEdit) ? "standard" : "outlined"}
                    disabled={!isEditing || isSaving || !canEdit}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("pImediata")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.pImediata}
                    helperText={methods.formState.errors.pImediata?.message}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label={t("captures.capture.protection-zone.outras-capt")}                         
                    variant={(!isEditing || !canEdit) ? "standard" : "outlined"}
                    disabled={!isEditing || isSaving || !canEdit}
                    sx={{ ...formStyles }}
                    {...methods.register("outrasCaptImediata")}
                    error={!!methods.formState.errors.outrasCaptImediata}
                    helperText={methods.formState.errors.outrasCaptImediata?.message}
                  />
                </Grid2>

                <Typography variant="caption" color="text.secondary">
                  {t("captures.capture.protection-zone.outras-capt-imediata.info")}
                </Typography>
                
                <Grid2 size={12} sx={{ mt: 4 }}>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 2.5,
                      display: "flex",
                      gap: 5,
                    }}
                  >
                    <Controller
                      name="snAimediata"
                      control={methods.control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              size="medium"
                            />
                          }
                          disabled={!isEditing || isSaving || !canEdit}
                          label={
                            <Typography fontWeight={400}>
                              {t("captures.capture.protection-zone.sn-a-imediata")}
                            </Typography>
                          }
                          sx={{ mr: 0 }}
                        />
                      )}
                    />

                    <TextField
                      fullWidth
                      label={t("captures.capture.protection-zone.estado")}
                      variant={(!isEditing || !canEdit) ? "standard" : "outlined"}
                      disabled={!isEditing || isSaving || !canEdit}
                      sx={{ ...formStyles }}
                      {...methods.register("estado")}
                      error={!!methods.formState.errors.estado}
                      helperText={methods.formState.errors.estado?.message}
                    />
                  </Box>
                </Grid2>
              </Grid2>

              <Grid2 size={12} sx={{ mt: 6, mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{fontWeight: 600, width: "100%" }}
                >
                  {t("captures.capture.protection-zone.to-materialize.info")}
                </Typography>
              </Grid2>

              <Grid2 container spacing={2}>
                <Grid2 size={4}>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 2.5,
                      display: "flex",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <Controller
                      name="rede"
                      control={methods.control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox {...field} checked={field.value} />}
                          disabled={!isEditing || isSaving || !canEdit}
                          label={
                            <Typography>
                              {t("captures.capture.protection-zone.rede")}
                            </Typography>
                          }
                          sx={{ mr: 0 }}
                        />
                      )}
                    />
                  </Box>
                </Grid2>

                <Grid2 size={8}>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 2.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      height: "100%",
                    }}
                  >
                    <Grid2 container spacing={2} alignItems="center">
                      <Grid2 size={7}>
                        <Controller
                          name="placa"
                          control={methods.control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={<Checkbox {...field} checked={field.value} />}
                              disabled={!isEditing || isSaving || !canEdit}
                              label={
                                <Typography>
                                  {t("captures.capture.protection-zone.placa")}
                                </Typography>
                              }
                              sx={{ mr: 0 }}
                            />
                          )}
                        />
                      </Grid2>
                      <Grid2 size={5}>
                        <TextField
                          fullWidth
                          label={t("captures.capture.protection-zone.n-placa")}
                          variant={(!isEditing || !canEdit) ? "standard" : "outlined"}
                          disabled={!isEditing || isSaving || !canEdit}
                          sx={{ ...formStyles }}
                          type="text"
                          inputMode="decimal"
                          {...methods.register("nPlaca")}
                          error={!!methods.formState.errors.nPlaca}
                          helperText={methods.formState.errors.nPlaca?.message}
                        />
                      </Grid2>
                    </Grid2>
                  </Box>
                </Grid2>
              </Grid2>
            </Paper>
          </Grid2>

          <Grid2 size={12}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600, mb: 2 }}>
                {t("captures.capture.protection-zone.intemediate-zone")}
              </Typography>

              <Grid2 container spacing={2}>
                <Grid2 size={3}>
                  <TextField
                    fullWidth
                    label={t("captures.capture.protection-zone.area")}                         
                    variant={(!isEditing || !canEdit) ? "standard" : "outlined"}
                    disabled={!isEditing || isSaving || !canEdit}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("aIntermedia")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.ha")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.aIntermedia}
                    helperText={methods.formState.errors.aIntermedia?.message}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label={t("captures.capture.protection-zone.outras-capt")}                         
                    variant={(!isEditing || !canEdit) ? "standard" : "outlined"}
                    disabled={!isEditing || isSaving || !canEdit}
                    sx={{ ...formStyles }}
                    {...methods.register("outrasCaptIntermedia")}
                    error={!!methods.formState.errors.outrasCaptIntermedia}
                    helperText={methods.formState.errors.outrasCaptIntermedia?.message}
                  />
                </Grid2>
              </Grid2>
            </Paper>
          </Grid2>

          <Grid2 size={12}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600, mb: 2 }}>
                {t("captures.capture.protection-zone.extended-zone")}
              </Typography>

              <Grid2 container spacing={2}>
                <Grid2 size={3}>
                  <TextField
                    fullWidth
                    label={t("captures.capture.protection-zone.area")}                         
                    variant={(!isEditing || !canEdit) ? "standard" : "outlined"}
                    disabled={!isEditing || isSaving || !canEdit}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("aAlargada")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.ha")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.aAlargada}
                    helperText={methods.formState.errors.aAlargada?.message}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label={t("captures.capture.protection-zone.outras-capt")}                         
                    variant={(!isEditing || !canEdit) ? "standard" : "outlined"}
                    disabled={!isEditing || isSaving || !canEdit}
                    sx={{ ...formStyles }}
                    {...methods.register("outrasCaptAlargada")}
                    error={!!methods.formState.errors.outrasCaptAlargada}
                    helperText={methods.formState.errors.outrasCaptAlargada?.message}
                  />
                </Grid2>
              </Grid2>
            </Paper>
          </Grid2>

          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("captures.capture.protection-zone.material")}</TableCell>
                  <TableCell>{t("captures.capture.protection-zone.quantity")}</TableCell>
                  <TableCell align="center">{t("captures.capture.protection-zone.unit-price")}</TableCell>
                  <TableCell>{t("captures.capture.protection-zone.total-price")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  { 
                    id: "electrowelded-mesh", 
                    material: t("captures.capture.protection-zone.electrowelded-mesh"), 
                    key: "precoRede" 
                  },
                  { 
                    id: "bollards", 
                    material: t("captures.capture.protection-zone.bollards"), 
                    key: "precoPilaretes" 
                  },
                  { 
                    id: "identification-sign", 
                    material: t("captures.capture.protection-zone.identification-sign"),
                    key: "precoPlaca" 
                  },
                ].map((row) => {
                  
                  const pImediata = Number(methods.watch("pImediata") ?? 0);

                  let quantity: number;
                  if (row.id === "electrowelded-mesh") {
                    quantity = Number(methods.watch("pImediata") ?? 0);
                  } else if (row.id === "bollards") {
                    quantity = Number((pImediata / 3).toFixed(3));
                  } else {
                    quantity = Number(methods.watch("nPlaca") ?? 0);
                  }
                  
                  const unitPrice = Number(
                    methods.watch(row.key as keyof ProtectionZoneFormType) ?? 0
                  );

                  let totalPrice: number | undefined;
                  if (row.id === "electrowelded-mesh") {
                    totalPrice = protectionZoneData?.precoRedeCapt ?? undefined;
                  } else if (row.id === "bollards") {
                    totalPrice = protectionZoneData?.precoPilaretesCapt ?? undefined;
                  } else {
                    totalPrice = protectionZoneData?.precoPlacasCapt ?? undefined;
                  }

                  return (
                    <TableRow key={row.id}>
                      <TableCell>{row.material}</TableCell>
                      <TableCell align="center">{quantity}</TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          size="small"
                          variant={isEditing ? "outlined" : "standard"}
                          sx={{ ...formStyles }}
                          value={unitPrice}
                          disabled={!isEditing || !canEdit}
                          onChange={(e) => {
                            let value = Number(e.target.value);
                            if (value <= 0 || Number.isNaN(value)) value = 0;
                            methods.setValue(row.key as any, value);
                          }}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">€</InputAdornment>
                              ),
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {totalPrice == null ? "-" : `${totalPrice.toFixed(2)} €`}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              color="primary.main"
            >
              {t("captures.capture.protection-zone.total-price")}
            </Typography>

            <Typography
              variant="body1"
              fontWeight={600}
              sx={{ minWidth: 80, textAlign: "right" }}
            >
              {protectionZoneData?.precoCapt == null
                ? "-"
                : `${protectionZoneData.precoCapt.toFixed(2)} €`}
            </Typography>
          </Box>
        </Grid2>
      </Box>
    </FormProvider>
  );
}